import styled from '@emotion/styled'
import { useSelector } from 'app-redux'
import {
	FC,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react'
import { useParams } from 'react-router-dom'
import {
	MediaProgress,
	ProvideMediaProgressSink,
} from 'room/common/media-progress'
import { ProvideMediaReloadRequests } from 'room/common/media-reload'
import { ProvideStageAudioOutput } from 'room/common/stage-audio-output'
import { ProvideStageSurface } from 'room/common/stage-surface'
import { useJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useWorkflowUser } from 'room/common/use-workflow-user'
import { RoomProjectorInPerson } from 'room/training-room/room-projector-in-person'
import { useSessionMode } from 'session-mode/use-session-mode'
import { ProvideVideoComponents } from 'video-conference-media'
import { JunoProvider } from 'vcr/juno/juno-provider'
import { ProjectorHereMessage } from './model/presenter-messages'
import {
	FullscreenOffer,
	ProjectorCover,
	ProjectorNotice,
	ProjectorTitleCard,
} from './presentation/projector-cards'
import { useCloseWhenOrphaned } from './use-close-when-orphaned'
import { useIdleCursor } from './use-idle-cursor'
import { useProjectorFullscreen } from './use-projector-fullscreen'
import { useProjectorKeys } from './use-projector-keys'
import { useProjectorLink } from './use-projector-link'
import { useProjectorSpeakers } from './use-projector-speakers'
import { useScreenLabel } from './use-screen-label'

const InertBox = styled.div`
	display: contents;
`

/**
 * What the projector shows can be neither clicked, focused nor typed in: it
 * is fed by the console, never driven. A tool that forgets to leave out a
 * control (`StageControls`) therefore shows it at worst, but no click there
 * reaches the session.
 */
const Inert: FC = ({ children }) => {
	const box = useRef<HTMLDivElement>(null)
	useLayoutEffect(() => {
		box.current?.setAttribute('inert', '')
	}, [])
	return <InertBox ref={box}>{children}</InertBox>
}

/**
 * The presentation window's counterpart of `WorkflowRoute`: it never joins the
 * session, opens no event stream and sends nothing to the backend - the
 * state arrives from the console over the link, and key presses go back to
 * it. Until the first snapshot, and whenever the console is gone, the
 * projector shows the title card.
 *
 * The providers are the two `TrainingInteractionScheme` gives the stage; both
 * stay inert here (`JunoProvider` connects only with a conference id, which a
 * stationary scheme never sends).
 */
export const ProjectorRoot: FC<{ presentation: string }> = ({
	presentation,
}) => {
	const { workflowId } = useParams<{ workflowId: string }>()
	const { fullscreen, needsClick, goFullscreen } =
		useProjectorFullscreen(presentation)
	const screenLabel = useScreenLabel()

	const reportDisplay = useRef((): void => undefined)
	const reportSpeakers = useRef((): void => undefined)
	const [mediaReloads, setMediaReloads] = useState(0)
	const link = useProjectorLink(
		workflowId,
		presentation,
		() => {
			// A console that (re)connected learns where this window is and, from
			// the message's source, which window to hand full screen to.
			reportDisplay.current()
			reportSpeakers.current()
			const here: ProjectorHereMessage = {
				kind: 'projector-here',
				presentation,
			}
			window.opener?.postMessage(here, window.location.origin)
		},
		// The console's remote asks the recording to load its file again.
		() => setMediaReloads(count => count + 1)
	)
	const { post } = link
	reportDisplay.current = () =>
		post({
			kind: 'display',
			display: fullscreen ? 'fullscreen' : 'windowed',
			screenLabel,
		})
	useEffect(() => reportDisplay.current(), [fullscreen, screenLabel])

	// The recordings play here, so whether the trainer's speakers work is
	// known here; the console shows it.
	const speakers = useProjectorSpeakers()
	reportSpeakers.current = () =>
		post({ kind: 'speakers', fellBack: speakers.fellBack })
	useEffect(() => reportSpeakers.current(), [speakers.fellBack])

	useCloseWhenOrphaned(link.synced, link.consolePresent)
	useProjectorKeys({
		goFullscreen,
		command: command => post({ kind: 'command', command }),
	})
	const reportMedia = useCallback(
		(media: MediaProgress | null) => post({ kind: 'media', media }),
		[post]
	)
	useIdleCursor(fullscreen)

	const blank = useSelector(state => state.presenter.blank)
	const training = useSelector(state => state.sessionInfo.training)
	const room = useJoinedRoomAddress()
	const user = useWorkflowUser()
	const mode = useSessionMode()

	useEffect(() => {
		if (training?.subject) {
			document.title = training.subject
		}
	}, [training?.subject])

	const titleCard = (
		<ProjectorTitleCard subject={training?.subject} venue={training?.venue} />
	)
	const content = (() => {
		if (link.otherBuild) {
			return <ProjectorNotice messageKey="in_person.presenter.reload" />
		}
		if (!link.synced || !link.consolePresent || !room || !user) {
			return titleCard
		}
		if (mode && mode !== 'IN_PERSON') {
			return <ProjectorNotice messageKey="in_person.presenter.only_in_person" />
		}
		return (
			<JunoProvider>
				<ProvideVideoComponents>
					<RoomProjectorInPerson
						roomType={room.roomType}
						unsupported={titleCard}
					/>
				</ProvideVideoComponents>
			</JunoProvider>
		)
	})()

	return (
		<ProvideStageSurface surface="projector">
			<ProvideMediaProgressSink sink={reportMedia}>
				<ProvideMediaReloadRequests requests={mediaReloads}>
					<ProvideStageAudioOutput output={speakers.output}>
						<Inert>
							{content}
							{blank ? (
								<ProjectorCover data-testid="presenter.projector.black" />
							) : null}
						</Inert>
						{!fullscreen || needsClick ? (
							<FullscreenOffer onClick={goFullscreen} />
						) : null}
					</ProvideStageAudioOutput>
				</ProvideMediaReloadRequests>
			</ProvideMediaProgressSink>
		</ProvideStageSurface>
	)
}

export default ProjectorRoot
